/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import Dexie, { Table } from 'dexie'
import {
  Text,
  TextVersion,
  VersionDraft,
  Compilation,
  AudioFile,
  CachedAudioLine,
  Notification,
} from '../models/Play'
import { Group, GroupMember, GroupText, Invitation } from '../models/Group'
import { Settings, DEFAULT_SETTINGS } from '../models/Settings'
import type { VersionConfig } from '../models/VersionConfig'

/**
 * Utilisateur synchronisé (copie locale des infos backend)
 */
export interface SyncUser {
  id: string
  email: string
  displayName: string
  avatarUrl?: string
  createdAt: string
  lastLoginAt?: string
}

/**
 * Base de données IndexedDB pour Répét V2
 */
export class RepetDatabase extends Dexie {
  // === TABLES V2 ===
  users!: Table<SyncUser, string>
  texts!: Table<Text, string>
  textVersions!: Table<TextVersion, string>
  versionDrafts!: Table<VersionDraft, string>
  compilations!: Table<Compilation, string>
  audioFiles!: Table<AudioFile, string>
  audioCache!: Table<CachedAudioLine, string>
  groups!: Table<Group, string>
  groupMembers!: Table<GroupMember, string>
  groupTexts!: Table<GroupText, string>
  invitations!: Table<Invitation, string>
  notifications!: Table<Notification, string>
  settings!: Table<Settings, string>
  versionConfigs!: Table<VersionConfig, string>

  constructor() {
    super('RepetDB')

    // Schéma V1 - conservé pour l'historique mais non utilisé pour la migration
    this.version(1).stores({
      plays: 'id, title, createdAt, updatedAt',
      settings: 'id',
    })

    // Schéma V2 - idem
    this.version(2).stores({
      plays: 'id, title, createdAt, updatedAt, serverId, syncStatus',
      settings: 'id',
      users: 'id, email',
    })

    // Version 3 du schéma (V2 - Implémentation du versioning)
    // Pas de fonction .upgrade() car on ne migre pas les données V1/V2.
    this.version(3).stores({
      // Les anciennes tables sont implicitement supprimées par la nouvelle définition
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, serverId, deletedAt',
      textVersions: 'id, textId, createdById, contentHash, serverId, deviceId, deletedAt',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      groups: 'id, createdById, syncStatus, serverId',
      groupMembers: 'id, &[groupId+userId]',
      groupTexts: 'id, &[groupId+textId], serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
    })

    // Version 4 du schéma (V2.0.1 - Ajout VersionDraft pour édition collaborative)
    this.version(4).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, serverId, deletedAt',
      textVersions: 'id, textId, createdById, contentHash, serverId, deviceId, deletedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      groups: 'id, createdById, syncStatus, serverId',
      groupMembers: 'id, &[groupId+userId]',
      groupTexts: 'id, &[groupId+textId], serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
    })

    // Version 5 du schéma (V2.0.1 - Ajout invitations pour les groupes)
    this.version(5).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, serverId, deletedAt',
      textVersions: 'id, textId, createdById, contentHash, serverId, deviceId, deletedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      groups: 'id, ownerId, syncStatus, serverId',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
    })

    // Version 6 du schéma (V2.0.1 - Ajout audioCache pour les lignes audio mises en cache)
    this.version(6).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, serverId, deletedAt',
      textVersions: 'id, textId, createdById, contentHash, serverId, deviceId, deletedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
    })

    // Version 7 du schéma (V2.2 - Ajout index syncStatus sur textVersions pour les queries de sync)
    this.version(7).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, serverId, deletedAt',
      textVersions:
        'id, textId, createdById, contentHash, serverId, deviceId, deletedAt, syncStatus',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
    })

    // Version 8 du schéma (sync-rework - index updatedAt, suppression GSN)
    this.version(8).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, updatedAt, deletedAt',
      textVersions:
        'id, textId, createdById, contentHash, serverId, deviceId, deletedAt, syncStatus, updatedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
    })

    // Version 9 du schéma (prompt 06 — migration PlaySettings → versionConfigs)
    this.version(9).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, updatedAt, deletedAt',
      textVersions:
        'id, textId, createdById, contentHash, serverId, deviceId, deletedAt, syncStatus, updatedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
      versionConfigs: 'id, versionId, userId, syncStatus, updatedAt, &[versionId+userId]',
    })

    // Version 10 : isFavorite sur textVersions, lastOpenedAt sur groups
    this.version(10).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, updatedAt, deletedAt',
      textVersions:
        'id, textId, createdById, contentHash, serverId, deviceId, deletedAt, syncStatus, updatedAt, isFavorite, lastOpenedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId, lastOpenedAt',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
      versionConfigs: 'id, versionId, userId, syncStatus, updatedAt, &[versionId+userId]',
    })

    // Version 11 : index userId sur groupMembers pour les requêtes offline-first
    // (getLocalGroups cherche par userId, pas uniquement par [groupId+userId])
    // NOTE : cette version contient un bug (textId en doublon dans groupTexts) —
    // elle est conservée telle quelle pour les bases existantes à cette version.
    // Les bases fraîches passent directement à la v12.
    this.version(11).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, updatedAt, deletedAt',
      textVersions:
        'id, textId, createdById, contentHash, serverId, deviceId, deletedAt, syncStatus, updatedAt, isFavorite, lastOpenedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId, lastOpenedAt',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId, textId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
      versionConfigs: 'id, versionId, userId, syncStatus, updatedAt, &[versionId+userId]',
    })

    // Version 12 : correction du doublon textId dans groupTexts (v11 cassait les bases fraîches)
    this.version(12).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, updatedAt, deletedAt',
      textVersions:
        'id, textId, createdById, contentHash, serverId, deviceId, deletedAt, syncStatus, updatedAt, isFavorite, lastOpenedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId, lastOpenedAt',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
      versionConfigs: 'id, versionId',
    })

    // Version 13 : restaure l'index composé [versionId+userId] sur versionConfigs
    this.version(13).stores({
      users: 'id, email',
      texts: 'id, ownerId, syncStatus, lastOpenedAt, updatedAt, deletedAt',
      textVersions:
        'id, textId, createdById, contentHash, serverId, deviceId, deletedAt, syncStatus, updatedAt, isFavorite, lastOpenedAt',
      versionDrafts: 'id, versionId, userId, lastModifiedAt, syncStatus, serverId',
      compilations: 'id, textVersionId, userId, status, createdAt, serverId',
      audioFiles: 'id, compilationId, lineId',
      audioCache: 'hash, lastUsedAt, archivedAt',
      groups: 'id, ownerId, syncStatus, serverId, lastOpenedAt',
      groupMembers: 'id, groupId, userId, &[groupId+userId]',
      groupTexts: 'id, groupId, textId, &[groupId+textId], serverId',
      invitations: 'id, groupId, invitedEmail, status, serverId',
      notifications: 'id, userId, read, createdAt, serverId',
      settings: 'id',
      versionConfigs: 'id, versionId, userId, syncStatus, updatedAt, &[versionId+userId]',
    })
  }
}

/**
 * Instance singleton de la base de données
 */
export const db = new RepetDatabase()

/**
 * Vide toutes les données applicatives (textes, versions, drafts, compilations, audio).
 * Ne touche pas aux tables de schéma ni aux tokens d'auth.
 * Utilisé lors du passage sur la branche sync-rework pour repartir d'une base vierge.
 */
export async function clearAppData(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.texts,
      db.textVersions,
      db.versionDrafts,
      db.compilations,
      db.audioFiles,
      db.audioCache,
      db.versionConfigs,
    ],
    async () => {
      await db.texts.clear()
      await db.textVersions.clear()
      await db.versionDrafts.clear()
      await db.compilations.clear()
      await db.audioFiles.clear()
      await db.audioCache.clear()
      await db.versionConfigs.clear()
    }
  )

  // Vider le cache audio CacheStorage
  if ('caches' in window) {
    try {
      await caches.delete('repet-audio')
    } catch {
      // Non-bloquant si CacheStorage indisponible
    }
  }

  // Vider les clés localStorage liées aux données applicatives
  // Note: 'repet-play-settings-storage' est retiré — les configs sont maintenant en IndexedDB
  // lastSyncAt est scopé par userId (format 'lastSyncAt:<userId>') — supprimer toutes les variantes
  const keysToRemove = ['repet_text_order', 'lastSyncAt', 'deviceId']
  keysToRemove.forEach((k) => localStorage.removeItem(k))

  // Supprimer toutes les clés lastSyncAt:<userId> (scopées par utilisateur)
  const scopedSyncKeys = Object.keys(localStorage).filter((k) => k.startsWith('lastSyncAt:'))
  scopedSyncKeys.forEach((k) => localStorage.removeItem(k))
}

/**
 * Initialise la base de données (paramètres par défaut)
 * À appeler au démarrage de l'application
 */
export async function initializeDatabase(): Promise<void> {
  try {
    // Vérifier si les paramètres existent
    const existingSettings = await db.settings.get('global')

    if (!existingSettings) {
      // Créer les paramètres par défaut
      await db.settings.add(DEFAULT_SETTINGS)
    }
  } catch (error) {
    console.error("Erreur lors de l'initialisation de la base de données:", error)
    throw new Error(
      `Impossible d'initialiser la base de données : ${
        error instanceof Error ? error.message : 'Erreur inconnue'
      }`
    )
  }
}
